import { supabase } from '@/lib/supabase';
import { SeoPage } from '@/types/api/seo';

function mapRow(row: any): SeoPage {
  return {
    id: row.id,
    projectId: row.project_id,
    url: row.url,
    path: row.path,
    title: row.title,
    metaDescription: row.meta_description,
    wpPostId: row.wp_post_id,
    wpPostType: row.wp_post_type,
    source: row.source ?? 'gsc',
    automationEnabled: row.automation_enabled ?? false,
    seoTrack: row.seo_track ?? null,
    contentUpdatedAt: row.content_updated_at,
    lastSeenAt: row.last_seen_at,
    createdAt: row.created_at,
  };
}

const seoPagesService = {
  async getByProject(projectId: string): Promise<SeoPage[]> {
    const { data, error } = await supabase
      .from('seo_pages')
      .select('*')
      .eq('project_id', projectId)
      .order('path', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async search(projectId: string, query: string): Promise<SeoPage[]> {
    const { data, error } = await supabase
      .from('seo_pages')
      .select('*')
      .eq('project_id', projectId)
      .or(`path.ilike.%${query}%,title.ilike.%${query}%,url.ilike.%${query}%`)
      .order('path', { ascending: true })
      .limit(20);

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async bulkUpsertFromGSC(
    projectId: string,
    pages: Array<{ url: string; title?: string }>
  ): Promise<SeoPage[]> {
    const rows = pages.map((p) => {
      let path = '';
      try {
        path = new URL(p.url).pathname;
      } catch {
        path = p.url;
      }
      return {
        project_id: projectId,
        url: p.url,
        path,
        title: p.title ?? null,
        last_seen_at: new Date().toISOString(),
      };
    });

    const { data, error } = await supabase
      .from('seo_pages')
      .upsert(rows, { onConflict: 'project_id,url' })
      .select();

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async toggleAutomation(id: string, enabled: boolean): Promise<void> {
    const { error } = await supabase
      .from('seo_pages')
      .update({ automation_enabled: enabled })
      .eq('id', id);

    if (error) throw error;
  },

  async updateTrack(id: string, track: 'blog' | 'service' | null): Promise<void> {
    const { error } = await supabase.from('seo_pages').update({ seo_track: track }).eq('id', id);

    if (error) throw error;
  },

  async bulkToggleAutomation(ids: string[], enabled: boolean): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from('seo_pages')
      .update({ automation_enabled: enabled })
      .in('id', ids);

    if (error) throw error;
  },

  async getById(id: string): Promise<SeoPage> {
    const { data, error } = await supabase.from('seo_pages').select('*').eq('id', id).single();

    if (error) throw error;
    return mapRow(data);
  },
};

export default seoPagesService;
